import { useEffect, useState, useCallback, useRef } from "react";
import {
 DialogContent,
 DialogActions,
 Button,
 Typography,
 CircularProgress,
 Box,
} from "@mui/material";
import { disableNetwork, enableNetwork } from "firebase/firestore";
import { databasePromise } from "configuration";
import { BaseDialog } from "./base";

export function OfflineDialog() {
 const databaseRef = useRef(null);
 const [isOffline, setIsOffline] = useState(!navigator.onLine);
 const [isChecking, setIsChecking] = useState(false);

 useEffect(() => {
  databasePromise.then((db) => {
   databaseRef.current = db;
  });
 }, []);

 const verifyActualConnection = useCallback(async () => {
  if (!navigator.onLine) {
   setIsOffline(true);
   return false;
  }

  setIsChecking(true);
  try {
   await fetch(`${process.env.REACT_APP_API_URL}/health`, {
    mode: "no-cors",
    method: "HEAD",
    cache: "no-store",
   });

   if (databaseRef.current) await enableNetwork(databaseRef.current);
   setIsOffline(false);
   return true;
  } catch (error) {
   setIsOffline(true);
   return false;
  } finally {
   setIsChecking(false);
  }
 }, []);

 useEffect(() => {
  const handleOffline = async () => {
   setIsOffline(true);
   try {
    if (databaseRef.current) await disableNetwork(databaseRef.current);
   } catch (e) {
    console.error("Failed to disable network", e);
   }
  };

  const handleOnline = async () => {
   verifyActualConnection();
  };

  window.addEventListener("offline", handleOffline);
  window.addEventListener("online", handleOnline);

  if (!navigator.onLine) {
   handleOffline();
  }

  return () => {
   window.removeEventListener("offline", handleOffline);
   window.removeEventListener("online", handleOnline);
  };
 }, [verifyActualConnection]);

 return (
  <BaseDialog open={isOffline} title="Connection Lost" onClose={() => {}}>
   <DialogContent>
    <Box
     sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 2,
      pt: 1,
      minWidth: 280,
     }}
    >
     <Typography variant="body1" textAlign="center">
      You are currently offline. Please check your internet connection to
      continue using the app.
     </Typography>

     {isChecking && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
       <CircularProgress size={20} />
       <Typography variant="caption" color="text.secondary">
        Verifying connection...
       </Typography>
      </Box>
     )}
    </Box>
   </DialogContent>

   <DialogActions sx={{ pb: 2, px: 3, justifyContent: "center", gap: 1 }}>
    <Button
     onClick={verifyActualConnection}
     disabled={isChecking}
     variant="outlined"
     fullWidth
    >
     {isChecking ? "Checking..." : "Retry Now"}
    </Button>
    <Button
     variant="contained"
     onClick={() => window.location.reload()}
     disabled={isChecking}
     fullWidth
    >
     Refresh Page
    </Button>
   </DialogActions>
  </BaseDialog>
 );
}
